import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Props {
  open: boolean;
  initialName: string;
  title?: string;
  onCancel: () => void;
  onSubmit: (name: string) => void;
  submitting?: boolean;
}

export function DoctorNameDialog({
  open,
  initialName,
  title,
  onCancel,
  onSubmit,
  submitting,
}: Props) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('aptCounter.doctorOnDuty');
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName(initialName);
      // Autofocus + select-all so users can type over an existing name.
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [open, initialName]);

  if (!open) return null;

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    onSubmit(trimmed);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={resolvedTitle}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-t-2xl bg-paper p-5 sm:rounded-2xl">
        <div className="text-[15px] font-semibold tracking-[-0.01em] text-ink">{resolvedTitle}</div>
        <p className="mt-1 text-[12.5px] text-muted">{t('aptCounter.doctorDialogDesc')}</p>
        <form
          className="mt-4 flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Input
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('aptCounter.doctorNamePlaceholder')}
            aria-label={t('aptCounter.doctorName')}
            autoComplete="off"
            maxLength={120}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting || name.trim().length === 0}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
